
import { defineComponent } from "vue";
import { mapState } from "vuex";

import Loader from "@/components/Loader.vue";
import RGPDLink from "@/components/RGPDLink.vue";
import GcuLink from "@/components/gcuLink.vue";
import RGPDPopin from "@/components/RGPDPopin.vue";
import { State } from "@/store";
import Consents from "@/models/Consents";
import RemindsMeCheckbox from "@/components/RemindsMeCheckbox.vue";

export default defineComponent({
    name: "Signup",
    components: {
        Loader,
        GcuLink,
        RGPDLink,
        RGPDPopin,
        RemindsMeCheckbox
    },
    data() {
        return {
            firstName: "",
            lastName: "",
            email: "",
            password1: "",
            password2: "",
            passType: `password`,
            isPassHidden: true,
            isPending: false,
            error: "",
            messageTitle: "",
            messageText: "",
            messagePromiseResolve: null as Function | null,
            rgpdPopinDisplayed: false,
            termsAndConditionsConsent: null,
            shareEmailConsent: null,
            remindsMe: false,
            passwordInvalid: false,
            passwordValidations: {
                pwdLength: false,
                pwdNumberRequired: false,
                pwdUpperRequired: false,
                pwdNoSpace: false
            },
            pwdNoMatch: false,
            showActivationForm: false,
            code: ""
        };
    },
    computed: {
        formInvalid() {
            return (
                !this.firstName.length ||
                !this.lastName.length ||
                !this.email.length ||
                !this.password1.length ||
                this.passwordInvalid ||
                this.pwdNoMatch
            );
        },
        activateformInvalid() {
            return (
                !this.code.length
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            authenticated: (state: State) => state.auth.authenticated,
            guest: (state: State) => state.auth.guest,
            didSignUp: (state: State) => state.auth.didSignUp
        })
    },
    watch: {
        async authenticated() {
            if (this.authenticated) {
                this.isPending = false;

                if (this.didSignUp) {
                    await this.$store.dispatch("rgpd/updateConsents", {
                        qrcodeId: this.qrcodeObject.qrcodeId,
                        shareEmailConsent: this.shareEmailConsent,
                        termsAndConditionsConsent: this.termsAndConditionsConsent
                    });

                    if (
                        this.$t("signInUp.onSignUpPopinText").length ||
                        this.$t("signInUp.onSignUpPopinTitle")
                    ) {
                        this.displayMessage(
                            this.$t("signInUp.onSignUpPopinText"),
                            this.$t("signInUp.onSignUpPopinTitle")
                        ).then(() => {
                            this.$router.replace("Upload");
                        });
                    } else {
                        this.$router.replace("Upload");
                    }
                } else {
                    this.$router.replace("Upload");
                }
            }
        },

        password1(value) {
            this.validatePwd(value);
        },

        password2() {
            this.pwdNoMatch = this.password1 !== this.password2;
        }
    },
    mounted() {
        if (!this.qrcodeObject.project.isActive) {
            this.$router.replace("/signin");
        }
    },
    methods: {
        async submitForm() {
            this.displayRGPDPopin();
        },

        showPassword() {
            if (this.isPassHidden) {
                this.passType = `text`;
                this.isPassHidden = false;
            } else {
                this.passType = `password`;
                this.isPassHidden = true;
            }
        },

        displayMessage(text: string, title = ""): Promise<void> {
            return new Promise(resolve => {
                this.messagePromiseResolve = resolve;
                this.messageText = text;
                this.messageTitle = title;
            });
        },

        closeMessagePopin() {
            this.messageText = "";
            this.messageTitle = "";

            if (this.messagePromiseResolve) {
                this.messagePromiseResolve();
                this.messagePromiseResolve = null;
            }
        },

        displayRGPDPopin() {
            this.rgpdPopinDisplayed = true;
        },

        async onRGPDClosed(consents: Consents) {
            this.rgpdPopinDisplayed = false;

            if (!consents.termsAndConditionsConsent) {
                this.displayRGPDPopin();
                return;
            }

            this.error = "";
            this.isPending = true;
            try {
                this.termsAndConditionsConsent = consents.termsAndConditionsConsent;
                this.shareEmailConsent = consents.shareEmailConsent;

                await this.$store.dispatch("auth/register", {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    password: this.password1,
                    remindsMe: this.remindsMe
                });
                this.showActivationForm = true;
                this.isPending = false;
            } catch (e) {
                this.isPending = false;
                this.error = this.$t("signInUp." + e.message);
            }
        },

        async sendCode(){
            this.$store.dispatch("auth/check");
            try{
                if(this.guest){
                    await this.$store.dispatch("auth/activate", {
                        id: this.guest.id,
                        code: this.code,
                    });
                    this.$router.replace("/signin");
                }
            } catch(e){
                this.error = this.$t("signInUp.activationError") + e;
            }
        },

        updateRemindsMe(value) {
            this.remindsMe = value;
        },

        validatePwd(value) {
            const numberRegex = /\d/;
            const caseRegex = /(?=.*[a-z])(?=.*[A-Z])/;
            const lengthRegex = /.{8,12}/;
            const noSpaceRegex = /^[^\s]*$/;

            this.passwordValidations.pwdLength = lengthRegex.test(value);
            this.passwordValidations.pwdUpperRequired = caseRegex.test(value);
            this.passwordValidations.pwdNumberRequired = numberRegex.test(value);
            this.passwordValidations.pwdNoSpace = noSpaceRegex.test(value);

            this.passwordInvalid =
                Object.values(this.passwordValidations).find(value => value === false) !==
                undefined;

            this.pwdNoMatch = this.password1 !== this.password2;
        },

        validateEmail(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        }
    }
});
